import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  getProductions,
  getProduction,
  createProduction,
  updateProduction,
  deleteProduction,
  addVariantAllocation,
  deleteVariantAllocation,
  getMilkBatches,
  getTemplates,
  getPlans,
  getStdPlans,
  getProductionStatuses,
  createProductionStatus,
  updateProductionStatus,
  deleteProductionStatus,
  reorderProductionStatuses,
  downloadPdf,
  getPackagingOptions,
  getOperators,
  getSubproductLots,
  getRawMaterialLots,
  getProductSettings,
  getCompanySettings,
  downloadProductionsXlsx,
  fmt,
} from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useProductTypes } from "@/context/ProductTypesContext";
import { Plus, FlaskConical, Trash2, AlertTriangle, FileText, Pencil, X, Filter, SlidersHorizontal, Settings2, ChevronUp, ChevronDown, Check, List, LayoutGrid, CalendarClock, Snowflake, ThermometerSnowflake, Tag, ArrowDownToLine, Droplets, Scale, FileSpreadsheet } from "lucide-react";
import { ResultsFilter } from "@/components/ResultsFilter";
import { StatusManagerDialog } from "./productions/StatusManagerDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { useT } from "@/i18n/I18nContext";
import { formatDate } from "@/lib/dates";
import { usePagination } from "@/hooks/usePagination";
import { TablePagination } from "@/components/TablePagination";

const ALL = "all";

// Yield-efficiency colour thresholds: green >=95%, amber 80-95%, red <80%
const effTone = (pct) =>
  pct == null
    ? ""
    : pct >= 95
    ? "badge-ok"
    : pct >= 80
    ? "bg-[hsl(var(--qc-warn-bg))] text-[hsl(var(--warning))]"
    : "badge-bad";

function EfficiencyBadge({ pct }) {
  if (pct == null) return <span className="text-muted-foreground">—</span>;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${effTone(pct)}`} data-testid="efficiency-badge">
      {pct}%
    </span>
  );
}

// Soft colored pill for a workflow status (looked up by label in the statuses config)
function StatusPill({ label, statuses, className = "" }) {
  const s = (statuses || []).find((x) => x.label === label);
  const color = s?.color || "#64748B";
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium ${className}`}
      style={{ color, backgroundColor: `${color}1f`, borderColor: `${color}40` }}
      data-testid="status-pill"
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
      {label || "\u2014"}
    </span>
  );
}

// Inline quick-change status dropdown (used on each production row)
function RowStatusSelect({ production, statuses, onChanged }) {
  const { t } = useT();
  const [saving, setSaving] = useState(false);
  const change = async (label) => {
    if (label === production.status) return;
    setSaving(true);
    try {
      await updateProduction(production.id, { status: label });
      toast.success(t("prod.statusArrow", { label }));
      onChanged();
    } catch {
      toast.error(t("prod.failedStatus"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Select value={production.status} onValueChange={change} disabled={saving}>
        <SelectTrigger className="h-8 w-[180px] border-0 bg-transparent p-0 hover:bg-secondary/60 focus:ring-1" data-testid={`row-status-select-${production.id}`}>
          <StatusPill label={production.status} statuses={statuses} />
        </SelectTrigger>
        <SelectContent>
          {(statuses || []).map((s) => (
            <SelectItem key={s.id} value={s.label} data-testid={`row-status-option-${s.label}`}>
              <span className="inline-flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                {s.label}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function Productions() {
  const { can } = useAuth();
  const { t, td } = useT();
  const { productTypes } = useProductTypes();
  const canManageStatuses = can("admin", "manager");
  const [productions, setProductions] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [statusManagerOpen, setStatusManagerOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [prefillPlan, setPrefillPlan] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters: product type, month (YYYY-MM), specific date (YYYY-MM-DD)
  const [productFilter, setProductFilter] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [monthFilter, setMonthFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [maturationOnly, setMaturationOnly] = useState(false);

  const load = async () => setProductions(await getProductions());
  const loadStatuses = async () => setStatuses(await getProductionStatuses());
  useEffect(() => {
    (async () => { await load(); await loadStatuses(); })();
    // Intentional run-once mount load; load/loadStatuses are stable here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtersActive = productFilter !== ALL || statusFilter !== ALL || monthFilter !== "" || dateFilter !== "" || expiringOnly || maturationOnly;
  const clearFilters = () => {
    setProductFilter(ALL);
    setStatusFilter(ALL);
    setMonthFilter("");
    setDateFilter("");
    setExpiringOnly(false);
    setMaturationOnly(false);
  };

  // Excel export of the (filtered) production runs incl. specific consumption — admin/manager only.
  const [exporting, setExporting] = useState(false);
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const params = {};
      if (productFilter !== ALL) params.product = productFilter;
      if (statusFilter !== ALL) params.status = statusFilter;
      if (dateFilter) params.date = dateFilter;
      else if (monthFilter) params.month = monthFilter;
      await downloadProductionsXlsx(params);
      toast.success(t("prod.exportDone"));
    } catch {
      toast.error(t("prod.exportError"));
    } finally {
      setExporting(false);
    }
  };

  const EXPIRY_WINDOW_DAYS = 7;
  const daysUntilExpiry = (p) => {
    if (!p.expiration_date) return null;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const exp = new Date(`${p.expiration_date}T00:00:00`);
    return Math.round((exp - today) / 86400000);
  };

  const filtered = useMemo(() => {
    return productions.filter((p) => {
      if (productFilter !== ALL && p.product_type !== productFilter) return false;
      if (statusFilter !== ALL && p.status !== statusFilter) return false;
      const day = (p.date || p.created_at || "").slice(0, 10); // YYYY-MM-DD
      if (dateFilter && day !== dateFilter) return false;
      if (monthFilter && day.slice(0, 7) !== monthFilter) return false;
      if (expiringOnly) {
        const d = daysUntilExpiry(p);
        if (d === null || d > EXPIRY_WINDOW_DAYS) return false;
      }
      if (maturationOnly) {
        if (!p.in_maturation || !p.maturation_ready_date || p.maturation_days_left == null || p.maturation_days_left > EXPIRY_WINDOW_DAYS) return false;
      }
      return true;
    });
  }, [productions, productFilter, statusFilter, monthFilter, dateFilter, expiringOnly, maturationOnly]);

  const pager = usePagination(filtered, 50);

  // QC columns selector: union of all QC field names across products' template snapshots
  const qcFieldNames = useMemo(() => {
    const set = new Set();
    productions.forEach((p) => (p.template_snapshot?.fields || []).forEach((f) => f?.name && set.add(f.name)));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [productions]);
  const [qcColumns, setQcColumns] = useState([]);
  const [showByproducts, setShowByproducts] = useState(false);
  const [showExpiry, setShowExpiry] = useState(false);
  const [showMaturation, setShowMaturation] = useState(false);
  const [showEfficiency, setShowEfficiency] = useState(false);
  const [viewMode, setViewMode] = useState("table"); // "table" | "board"
  const hasAnyByproducts = useMemo(
    () => productions.some((p) => (p.byproducts || []).some((b) => b && b.name)),
    [productions]
  );
  const toggleQcColumn = (name) =>
    setQcColumns((cols) => (cols.includes(name) ? cols.filter((c) => c !== name) : [...cols, name]));

  const qcCell = (p, name) => {
    const f = (p.template_snapshot?.fields || []).find((x) => x.name === name);
    if (!f) return "-";
    const v = p.qc_values?.[f.id];
    if (v === undefined || v === "" || v === null) return "-";
    if (typeof v === "boolean") return v ? t("common.yes") : t("common.no");
    return `${v}${f.unit ? " " + f.unit : ""}`;
  };
  const byproductCell = (p) => {
    const bps = (p.byproducts || []).filter((b) => b && b.name);
    if (!bps.length) return "-";
    return bps.map((b) => {
      const qty = b.yield_kg != null && b.yield_kg !== "" ? ` ${fmt(b.yield_kg)} ${b.unit === "l" ? "L" : "kg"}` : "";
      const code = b.lot_code ? ` (${b.lot_code})` : "";
      return `${b.name}${qty}${code}`;
    }).join(", ");
  };

  const openDetail = async (id) => {
    try {
      setDetail(await getProduction(id));
    } catch {
      toast.error(t("prod.notFound"));
    }
  };

  // Start production from a plan (?planId=...) and handle other deep-links
  useEffect(() => {
    (async () => {
      const consumed = [];
      const planId = searchParams.get("planId");
      if (planId) {
        const plans = await getPlans();
        const pl = plans.find((p) => p.id === planId);
        if (pl) {
          setPrefillPlan(pl);
          setWizardOpen(true);
        }
        consumed.push("planId");
      }
      // Pre-activate the "Expiring soon" filter when navigated from the dashboard (?expiring=1)
      if (searchParams.get("expiring")) {
        setExpiringOnly(true);
        setShowExpiry(true);
        consumed.push("expiring");
      }
      // Deep-link: open a production's QC file directly (?openId=<id>), e.g. from the planning calendar
      const openId = searchParams.get("openId");
      if (openId) {
        openDetail(openId);
        consumed.push("openId");
      }
      // Pre-activate the "Ready for cold room" filter when navigated from a maturation reminder (?maturation=1)
      if (searchParams.get("maturation")) {
        setMaturationOnly(true);
        setShowMaturation(true);
        consumed.push("maturation");
      }
      if (consumed.length) {
        // Build a fresh params object (no mutation/aliasing of the reactive searchParams)
        const next = new URLSearchParams();
        searchParams.forEach((value, key) => {
          if (!consumed.includes(key)) next.append(key, value);
        });
        setSearchParams(next, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout
      actions={
        <>
          {canManageStatuses && (
            <Button variant="outline" size="sm" onClick={() => setStatusManagerOpen(true)} data-testid="manage-statuses-button">
              <Settings2 className="mr-1.5 h-4 w-4" /> {t("prod.statuses")}
            </Button>
          )}
          <Button size="sm" onClick={() => { setPrefillPlan(null); setWizardOpen(true); }} data-testid="new-production-button">
            <Plus className="mr-1.5 h-4 w-4" /> {t("prod.newProduction")}
          </Button>
        </>
      }
    >
      <Card>
        <CardHeader className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-base">
              {t("prod.runsTitle")} ({filtered.length}{filtersActive ? ` ${t("prod.ofTotal", { n: productions.length })}` : ""})
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              {canManageStatuses && (
                <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={exportXlsx}
                  disabled={exporting} data-testid="productions-export-xlsx-button">
                  <FileSpreadsheet className="h-4 w-4" /> {t("prod.exportBtn")}
                </Button>
              )}
              <div className="flex items-center rounded-lg border border-border p-0.5">
                <Button
                  variant={viewMode === "table" ? "secondary" : "ghost"}
                  size="sm"
                  className="h-7 gap-1.5 px-2.5"
                  onClick={() => setViewMode("table")}
                  data-testid="view-table-button"
                >
                  <List className="h-4 w-4" /> {t("prod.table")}
                </Button>
                <Button
                  variant={viewMode === "board" ? "secondary" : "ghost"}
                  size="sm"
                  className="h-7 gap-1.5 px-2.5"
                  onClick={() => setViewMode("board")}
                  data-testid="view-board-button"
                >
                  <LayoutGrid className="h-4 w-4" /> {t("prod.board")}
                </Button>
              </div>
              {viewMode === "table" && (
                <QCColumnsSelector
                  names={qcFieldNames}
                  selected={qcColumns}
                  onToggle={toggleQcColumn}
                  hasByproducts={hasAnyByproducts}
                  showByproducts={showByproducts}
                  onToggleByproducts={() => setShowByproducts((s) => !s)}
                  showExpiry={showExpiry}
                  onToggleExpiry={() => setShowExpiry((s) => !s)}
                  showMaturation={showMaturation}
                  onToggleMaturation={() => setShowMaturation((s) => !s)}
                  showEfficiency={showEfficiency}
                  onToggleEfficiency={() => setShowEfficiency((s) => !s)}
                  onClear={() => { setQcColumns([]); setShowByproducts(false); setShowExpiry(false); setShowMaturation(false); setShowEfficiency(false); }}
                />
              )}
            </div>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
            <ResultsFilter
              selectLabel={t("common.product")}
              selectValue={productFilter}
              onSelectChange={setProductFilter}
              selectOptions={productTypes.map((p) => ({ value: p, label: td(p) }))}
              allLabel={t("prod.allProducts")}
              selectTestId="filter-product-select"
              monthValue={monthFilter}
              onMonthChange={setMonthFilter}
              monthTestId="filter-month-input"
              dateValue={dateFilter}
              onDateChange={setDateFilter}
              dateTestId="filter-date-input"
              active={filtersActive}
              onClear={clearFilters}
              clearTestId="filter-clear-button"
            />
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">{t("common.status")}</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[190px]" data-testid="filter-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t("prod.allStatuses")}</SelectItem>
                  {statuses.map((s) => (
                    <SelectItem key={s.id} value={s.label}>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                        {s.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">{t("prod.shelfLife")}</Label>
              <Button
                type="button"
                variant={expiringOnly ? "default" : "outline"}
                onClick={() => {
                  setExpiringOnly((s) => {
                    const next = !s;
                    if (next) setShowExpiry(true);
                    return next;
                  });
                }}
                data-testid="filter-expiring-toggle"
                className="gap-1.5"
              >
                <CalendarClock className="h-4 w-4" /> {t("dashboard.expiringSoon")}
              </Button>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">{t("prod.maturationLabel")}</Label>
              <Button
                type="button"
                variant={maturationOnly ? "default" : "outline"}
                onClick={() => {
                  setMaturationOnly((s) => {
                    const next = !s;
                    if (next) setShowMaturation(true);
                    return next;
                  });
                }}
                data-testid="filter-maturation-toggle"
                className="gap-1.5"
              >
                <Snowflake className="h-4 w-4" /> {t("dashboard.readyForColdRoom")}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {productions.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center">
              <FlaskConical className="h-10 w-10 text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">{t("prod.emptyStart")}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center" data-testid="filter-empty-state">
              <Filter className="h-10 w-10 text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">{t("prod.noMatch")}</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={clearFilters} data-testid="filter-empty-clear">
                {t("prod.clearFilters")}
              </Button>
            </div>
          ) : viewMode === "board" ? (
            <KanbanBoard productions={filtered} statuses={statuses} onChanged={load} onOpen={openDetail} />
          ) : (
            <div className="overflow-x-auto thin-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common.code")}</TableHead>
                    <TableHead>{t("common.product")}</TableHead>
                    <TableHead>{t("prod.operator")}</TableHead>
                    <TableHead className="text-right">{t("prod.milkL")}</TableHead>
                    <TableHead className="text-right">{t("prod.yieldKg")}</TableHead>
                    {qcColumns.map((name) => (
                      <TableHead key={name} className="text-right whitespace-nowrap" data-testid={`qc-col-head-${name}`}>{name}</TableHead>
                    ))}
                    {showByproducts && <TableHead data-testid="byproducts-col-head">{t("prod.byproducts")}</TableHead>}
                    {showExpiry && <TableHead data-testid="expiry-col-head">{t("common.expiry")}</TableHead>}
                    {showMaturation && <TableHead data-testid="maturation-col-head">{t("prod.coldRoomReady")}</TableHead>}
                    {showEfficiency && <TableHead className="text-right" data-testid="efficiency-col-head">{t("prod.yieldEff")}</TableHead>}
                    <TableHead>QC</TableHead>
                    <TableHead>{t("common.status")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pager.pageItems.map((p) => (
                    <TableRow key={p.id} className="cursor-pointer" onClick={() => openDetail(p.id)} data-testid={`production-row-${p.id}`}>
                      <TableCell className="font-mono text-xs">{p.production_code}</TableCell>
                      <TableCell className="font-medium">{td(p.product_type)}</TableCell>
                      <TableCell className="text-muted-foreground">{p.operator_name || "-"}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{fmt(p.total_milk_l)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{fmt(p.yield_kg)}</TableCell>
                      {qcColumns.map((name) => (
                        <TableCell key={name} className="text-right font-mono tabular-nums whitespace-nowrap">{qcCell(p, name)}</TableCell>
                      ))}
                      {showByproducts && <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{byproductCell(p)}</TableCell>}
                      {showExpiry && (
                        <TableCell className="whitespace-nowrap font-mono text-xs" data-testid={`expiry-cell-${p.id}`}>
                          {p.expiration_date || <span className="text-muted-foreground">—</span>}
                        </TableCell>
                      )}
                      {showMaturation && (
                        <TableCell className="whitespace-nowrap font-mono text-xs" data-testid={`maturation-cell-${p.id}`}>
                          {p.maturation_ready_date ? (
                            <span className="inline-flex items-center gap-1.5">
                              {p.maturation_ready_date}
                              {p.in_maturation && p.maturation_days_left != null && (
                                <span
                                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${p.maturation_days_left <= 0 ? "badge-bad" : "bg-[hsl(var(--qc-warn-bg))] text-[hsl(var(--warning))]"}`}
                                >
                                  {p.maturation_days_left <= 0 ? t("prod.ready") : t("prod.daysShort", { n: p.maturation_days_left })}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      )}
                      {showEfficiency && (
                        <TableCell className="text-right whitespace-nowrap" data-testid={`efficiency-cell-${p.id}`}>
                          <EfficiencyBadge pct={p.yield_efficiency_pct} />
                        </TableCell>
                      )}
                      <TableCell>
                        <StatusBadge status={(p.qc_warnings || []).length ? "warn" : "ok"} label={(p.qc_warnings || []).length ? t("prod.flagN", { n: p.qc_warnings.length }) : t("prod.ok")} />
                      </TableCell>
                      <TableCell>
                        <RowStatusSelect production={p} statuses={statuses} onChanged={load} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                page={pager.page} pageCount={pager.pageCount} pageSize={pager.pageSize}
                total={pager.total} start={pager.start} end={pager.end}
                onPageChange={pager.setPage} onPageSizeChange={pager.setPageSize}
                testid="productions-pagination"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {wizardOpen && (
        <ProductionWizard
          prefillPlan={prefillPlan}
          onClose={() => { setWizardOpen(false); setPrefillPlan(null); }}
          onCreated={() => {
            setWizardOpen(false);
            setPrefillPlan(null);
            load();
          }}
        />
      )}

      <ProductionDetail
        detail={detail}
        statuses={statuses}
        onClose={() => setDetail(null)}
        onUpdated={async (id) => { await load(); if (id) setDetail(await getProduction(id)); else setDetail(null); }}
      />

      <StatusManagerDialog
        open={statusManagerOpen}
        statuses={statuses}
        onClose={() => setStatusManagerOpen(false)}
        onChanged={async () => { await loadStatuses(); await load(); }}
      />
    </Layout>
  );
}

// ---------------- QC columns selector ----------------
function QCColumnsSelector({ names, selected, onToggle, hasByproducts, showByproducts, onToggleByproducts, showExpiry, onToggleExpiry, showMaturation, onToggleMaturation, showEfficiency, onToggleEfficiency, onClear }) {
  const { t } = useT();
  const count = selected.length + (showByproducts ? 1 : 0) + (showExpiry ? 1 : 0) + (showMaturation ? 1 : 0) + (showEfficiency ? 1 : 0);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="qc-columns-button">
          <SlidersHorizontal className="mr-1.5 h-4 w-4" />
          {t("prod.columns")}{count > 0 ? ` (${count})` : ""}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-sm font-medium">{t("prod.extraColumns")}</span>
          {count > 0 && (
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onClear} data-testid="qc-columns-clear">
              {t("common.clear")}
            </Button>
          )}
        </div>
        <div className="max-h-72 overflow-y-auto thin-scroll p-2">
          <label
            className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm hover:bg-secondary"
            data-testid="expiry-column-option"
          >
            <Checkbox checked={showExpiry} onCheckedChange={onToggleExpiry} />
            <span className="truncate">{t("prod.expirationDate")}</span>
          </label>
          <label
            className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm hover:bg-secondary"
            data-testid="maturation-column-option"
          >
            <Checkbox checked={showMaturation} onCheckedChange={onToggleMaturation} />
            <span className="truncate">{t("prod.coldRoomReadyDate")}</span>
          </label>
          <label
            className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm hover:bg-secondary"
            data-testid="efficiency-column-option"
          >
            <Checkbox checked={showEfficiency} onCheckedChange={onToggleEfficiency} />
            <span className="truncate">{t("prod.yieldEfficiency")}</span>
          </label>
          {hasByproducts && (
            <label
              className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm hover:bg-secondary"
              data-testid="byproducts-column-option"
            >
              <Checkbox checked={showByproducts} onCheckedChange={onToggleByproducts} />
              <span className="truncate">{t("prod.byproductsYield")}</span>
            </label>
          )}
          <div className="my-1 border-t border-border" />
          <div className="px-2 py-1 text-xs font-medium text-muted-foreground">{t("prod.qcFields")}</div>
          {names.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">{t("prod.noQcFields")}</p>
          ) : (
            names.map((name) => (
              <label
                key={name}
                className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm hover:bg-secondary"
                data-testid={`qc-column-option-${name}`}
              >
                <Checkbox checked={selected.includes(name)} onCheckedChange={() => onToggle(name)} />
                <span className="truncate">{name}</span>
              </label>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}


// ---------------- Kanban board (grouped by status, drag to change status) ----------------
function KanbanBoard({ productions, statuses, onChanged, onOpen }) {
  const { t, td } = useT();
  const [dragId, setDragId] = useState(null);
  const [overLabel, setOverLabel] = useState(null);

  const grouped = useMemo(() => {
    const map = {};
    statuses.forEach((s) => { map[s.label] = []; });
    productions.forEach((p) => {
      if (!map[p.status]) map[p.status] = [];
      map[p.status].push(p);
    });
    return map;
  }, [productions, statuses]);

  const drop = async (label) => {
    setOverLabel(null);
    const id = dragId;
    setDragId(null);
    if (!id) return;
    const prod = productions.find((p) => p.id === id);
    if (!prod || prod.status === label) return;
    try {
      await updateProduction(id, { status: label });
      toast.success(t("prod.movedTo", { code: prod.production_code, label }));
      onChanged();
    } catch {
      toast.error(t("prod.failedMove"));
    }
  };

  return (
    <div className="flex gap-3 overflow-x-auto thin-scroll pb-2" data-testid="kanban-board">
      {statuses.map((s) => {
        const items = grouped[s.label] || [];
        return (
          <div
            key={s.id}
            className={`flex w-72 shrink-0 flex-col rounded-xl border bg-secondary/30 ${overLabel === s.label ? "border-[hsl(var(--brand))] ring-1 ring-[hsl(var(--brand))]" : "border-border"}`}
            onDragOver={(e) => { e.preventDefault(); setOverLabel(s.label); }}
            onDragLeave={() => setOverLabel((l) => (l === s.label ? null : l))}
            onDrop={() => drop(s.label)}
            data-testid={`kanban-column-${s.label}`}
          >
            <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2.5">
              <div className="flex items-center gap-2 truncate">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: s.color }} />
                <span className="truncate text-sm font-medium">{s.label}</span>
              </div>
              <Badge variant="secondary" className="shrink-0">{items.length}</Badge>
            </div>
            <div className="flex min-h-[120px] flex-1 flex-col gap-2 p-2">
              {items.length === 0 ? (
                <p className="px-2 py-6 text-center text-xs text-muted-foreground">{t("prod.dropHere")}</p>
              ) : (
                items.map((p) => (
                  <div
                    key={p.id}
                    draggable
                    onDragStart={() => setDragId(p.id)}
                    onDragEnd={() => { setDragId(null); setOverLabel(null); }}
                    onClick={() => onOpen(p.id)}
                    className="cursor-pointer rounded-lg border border-border bg-card p-2.5 shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing"
                    data-testid={`kanban-card-${p.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[11px] text-muted-foreground">{p.production_code}</span>
                      {(p.qc_warnings || []).length > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--warning))]">
                          <AlertTriangle className="h-3 w-3" />{p.qc_warnings.length}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-sm font-medium">{td(p.product_type)}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                      {p.yield_kg != null && <span>{fmt(p.yield_kg)} kg</span>}
                      {p.operator_name && <span>{p.operator_name}</span>}
                      {p.expiration_date && (
                        <span className="inline-flex items-center gap-1">
                          <CalendarClock className="h-3 w-3" /> {t("prod.exp")} {formatDate(p.expiration_date)}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ---------------- Wizard ----------------
function ProductionWizard({ onClose, onCreated, prefillPlan }) {
  const { t, td } = useT();
  const { productTypes } = useProductTypes();
  const [step, setStep] = useState("product");
  const [productType, setProductType] = useState(prefillPlan?.product_type || productTypes[0]);
  const [madeDate, setMadeDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [operator, setOperator] = useState(prefillPlan?.operator_name || "");
  const [yieldLines, setYieldLines] = useState(emptyYieldLines);
  const [packagings, setPackagings] = useState([]);
  const [notes, setNotes] = useState(prefillPlan?.notes || "");
  const [targetFat, setTargetFat] = useState(prefillPlan?.target_fat ?? "");
  const [batches, setBatches] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stdPlans, setStdPlans] = useState([]);
  const [inputs, setInputs] = useState(
    prefillPlan?.milk_batch_id ? [{ milk_batch_id: prefillPlan.milk_batch_id, qty_l: prefillPlan.planned_volume_l ?? "" }] : []
  );
  const [templateId, setTemplateId] = useState(prefillPlan?.template_id || null);
  const [qcValues, setQcValues] = useState({});
  const [qcCorrective, setQcCorrective] = useState({});
  const [byproducts, setByproducts] = useState([]);
  const [subInputs, setSubInputs] = useState([]);
  const [rmInputs, setRmInputs] = useState([]);
  const [operators, setOperators] = useState([]);
  const [saving, setSaving] = useState(false);
  const [freezerSet, setFreezerSet] = useState(() => new Set());

  useEffect(() => {
    getMilkBatches(true).then(setBatches);
    getStdPlans().then(setStdPlans);
    getOperators({ active: true }).then((ops) => setOperators(ops || []));
    getPackagingOptions().then((ps) => setPackagings((ps || []).filter((p) => p.active !== false)));
    getProductSettings().then((rows) => setFreezerSet(new Set((rows || []).filter((r) => r.freezer_storage).map((r) => r.product_type))));
    // Intentional run-once mount load of reference data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    getTemplates(productType).then((ts) => {
      setTemplates(ts);
      if (ts.length) setTemplateId((prev) => prev || ts[0].id);
    });
    // Reload templates only when the product type changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType]);

  const template = useMemo(() => templates.find((t) => t.id === templateId), [templates, templateId]);
  // Batches not yet selected as inputs — memoized so the dropdown list isn't rebuilt every render.
  const availableBatches = useMemo(
    () => batches.filter((b) => !inputs.some((i) => i.milk_batch_id === b.id)),
    [batches, inputs],
  );

  const applyStdPlan = (planId) => {
    const sp = stdPlans.find((p) => p.id === planId);
    if (!sp) return;
    if (sp.target_fat != null) setTargetFat(String(sp.target_fat));
    if (sp.milk_batch_id && !inputs.some((i) => i.milk_batch_id === sp.milk_batch_id)) {
      setInputs((arr) => [...arr, { milk_batch_id: sp.milk_batch_id, qty_l: sp.base_volume ?? "" }]);
    }
    toast.success(t("prod.appliedStd", { name: sp.name }));
  };

  const addInput = (batchId) => {
    if (inputs.some((i) => i.milk_batch_id === batchId)) return;
    setInputs((arr) => [...arr, { milk_batch_id: batchId, qty_l: "" }]);
  };
  const updateInputQty = (batchId, qty) =>
    setInputs((arr) => arr.map((i) => (i.milk_batch_id === batchId ? { ...i, qty_l: qty } : i)));
  const removeInput = (batchId) => setInputs((arr) => arr.filter((i) => i.milk_batch_id !== batchId));

  const totalMilk = inputs.reduce((s, i) => s + (Number(i.qty_l) || 0), 0);

  const fieldStatus = (f) => {
    const v = qcValues[f.id];
    if (v === undefined || v === "") return null;
    if (f.type === "select") return (f.fail_options || []).includes(v) ? "bad" : "ok";
    if (["number", "temperature", "ph", "time"].includes(f.type)) {
      const n = Number(v);
      if (Number.isNaN(n)) return null;
      if (f.min != null && n < f.min) return "bad";
      if (f.max != null && n > f.max) return "bad";
      return "ok";
    }
    return null;
  };

  const create = async () => {
    // validate inputs
    const cleanInputs = inputs
      .filter((i) => Number(i.qty_l) > 0)
      .map((i) => ({ milk_batch_id: i.milk_batch_id, qty_l: Number(i.qty_l) }));
    const cleanByproducts = byproducts
      .filter((b) => b.name.trim())
      .map((b) => ({ name: b.name.trim(), yield_kg: b.yield_kg === "" ? null : Number(b.yield_kg), unit: b.unit || "kg" }));
    const cleanSubInputs = (subInputs || [])
      .filter((r) => Number(r.qty) > 0)
      .map((r) => ({ lot_id: r.lot_id, qty: Number(r.qty) }));
    const cleanRmInputs = (rmInputs || [])
      .filter((r) => Number(r.qty) > 0)
      .map((r) => ({ lot_id: r.lot_id, qty: Number(r.qty) }));
    const cleanYield = cleanYieldLines(yieldLines);
    // Milk is optional (e.g. butter may be made from cream/by-products), but a run must record
    // at least some raw material (milk or a by-product) OR a produced yield.
    if (cleanInputs.length === 0 && cleanSubInputs.length === 0 && cleanYield.length === 0) {
      toast.error(t("prod.needRawOrYield"));
      setStep("milk");
      return;
    }
    // Fail-option discipline: a corrective action note is required for every non-conforming selection.
    const missingCorr = (template?.fields || []).find((f) =>
      f.type === "select" && (f.fail_options || []).includes(qcValues[f.id]) && !(qcCorrective[f.id] || "").trim());
    if (missingCorr) {
      toast.error(t("prod.correctiveRequired", { field: missingCorr.name }));
      setStep("qc");
      return;
    }
    setSaving(true);
    try {
      await createProduction({
        product_type: productType,
        date: madeDate ? new Date(madeDate).toISOString() : undefined,
        operator_name: operator,
        milk_inputs: cleanInputs,
        template_id: templateId,
        qc_values: qcValues,
        qc_corrective: qcCorrective,
        yield_breakdown: cleanYield.length ? cleanYield : null,
        target_fat: targetFat === "" ? null : Number(targetFat),
        byproducts: cleanByproducts,
        subproduct_inputs: cleanSubInputs,
        raw_material_inputs: cleanRmInputs,
        plan_id: prefillPlan?.id || null,
        notes,
      });
      toast.success(t("prod.created"));
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("prod.failedCreate"));
    } finally {
      setSaving(false);
    }
  };

  const batchById = (id) => batches.find((b) => b.id === id);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto thin-scroll">
        <DialogHeader>
          <DialogTitle>{prefillPlan ? t("prod.startPlanned") : t("prod.newRun")}</DialogTitle>
        </DialogHeader>

        <Tabs value={step} onValueChange={setStep}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="product" data-testid="wizard-tab-product">{t("prod.tabProduct")}</TabsTrigger>
            <TabsTrigger value="milk" data-testid="wizard-tab-milk">{t("prod.tabMilk")}</TabsTrigger>
            <TabsTrigger value="qc" data-testid="wizard-tab-qc">{t("prod.tabQc")}</TabsTrigger>
            <TabsTrigger value="review" data-testid="wizard-tab-review">{t("prod.tabReview")}</TabsTrigger>
          </TabsList>

          {/* Step 1 */}
          <TabsContent value="product" className="space-y-4 pt-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>{t("prod.productType")}</Label>
                <Select value={productType} onValueChange={setProductType}>
                  <SelectTrigger data-testid="wizard-product-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {productTypes.map((p) => <SelectItem key={p} value={p}>{td(p)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("prod.operator")}</Label>
                <Select value={operator || "__none__"} onValueChange={(v) => setOperator(v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="wizard-operator-input">
                    <SelectValue placeholder={t("prod.selectOperator")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t("prod.selectOperator")}</SelectItem>
                    {operator && !operators.some((o) => o.name === operator) && (
                      <SelectItem value={operator}>{operator}</SelectItem>
                    )}
                    {operators.map((o) => (
                      <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("prod.madeOnDate")}</Label>
                <Input type="date" value={madeDate} onChange={(e) => setMadeDate(e.target.value)} data-testid="wizard-date-input" />
                <p className="text-[11px] text-muted-foreground">{t("prod.madeOnHint")}</p>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setStep("milk")} data-testid="wizard-next-product">{t("prod.nextMilk")}</Button>
            </div>
          </TabsContent>

          {/* Step 2 */}
          <TabsContent value="milk" className="space-y-4 pt-4">
            {stdPlans.length > 0 && (
              <div className="space-y-1.5 rounded-lg border border-[hsl(var(--brand)/0.25)] bg-[hsl(var(--brand)/0.05)] p-3">
                <Label>{t("prod.applyStd")}</Label>
                <Select value="" onValueChange={applyStdPlan}>
                  <SelectTrigger data-testid="wizard-stdplan-select"><SelectValue placeholder={t("prod.loadStd")} /></SelectTrigger>
                  <SelectContent>
                    {stdPlans.map((sp) => (
                      <SelectItem key={sp.id} value={sp.id}>
                        {t("prod.stdOption", { name: sp.name, fat: sp.target_fat })}{sp.batch_code ? ` · ${sp.batch_code}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>{t("prod.addMilkBatch")}</Label>
              <Select value="" onValueChange={addInput}>
                <SelectTrigger data-testid="wizard-add-milk-select"><SelectValue placeholder={t("prod.selectBatch")} /></SelectTrigger>
                <SelectContent>
                  {availableBatches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {t("prod.batchOption", { code: b.batch_code, l: fmt(b.remaining_l), f: fmt(b.analysis?.fat, 2) })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {batches.length === 0 && <p className="text-xs text-muted-foreground">{t("prod.noMilk")}</p>}
            </div>

            <div className="space-y-2">
              {inputs.map((i) => {
                const b = batchById(i.milk_batch_id);
                const over = Number(i.qty_l) > (b?.remaining_l ?? 0);
                return (
                  <div key={i.milk_batch_id} className="flex items-center gap-3 rounded-lg border border-border p-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-xs text-muted-foreground">{b?.batch_code}</div>
                      <div className="text-sm">{t("prod.availFat", { l: fmt(b?.remaining_l), f: fmt(b?.analysis?.fat, 2) })}</div>
                    </div>
                    <div className="relative w-32">
                      <Input
                        type="number"
                        value={i.qty_l}
                        onChange={(e) => updateInputQty(i.milk_batch_id, e.target.value)}
                        className={`pr-8 text-right font-mono ${over ? "border-[hsl(var(--danger))]" : ""}`}
                        data-testid={`wizard-qty-${i.milk_batch_id}`}
                        placeholder="0"
                      />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">L</span>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeInput(i.milk_batch_id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                );
              })}
            </div>

            {/* By-products used as raw material (only for products configured to consume them) */}
            <SubproductInputEditor value={subInputs} onChange={setSubInputs} productType={productType} />

            {/* Raw materials (rennet, cultures, salt, ...) consumed in this batch */}
            <RawMaterialInputEditor value={rmInputs} onChange={setRmInputs} />

            <div className="flex items-center justify-between border-t border-border pt-3">
              <span className="text-sm text-muted-foreground">{t("prod.totalMilk")} <span className="font-mono font-semibold text-foreground">{fmt(totalMilk)} L</span></span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep("product")}>{t("common.back")}</Button>
                <Button onClick={() => setStep("qc")} data-testid="wizard-next-milk">{t("prod.nextQc")}</Button>
              </div>
            </div>
          </TabsContent>

          {/* Step 3 */}
          <TabsContent value="qc" className="space-y-4 pt-4">
            <div className="space-y-1.5">
              <Label>{t("prod.qcTemplate")}</Label>
              <Select value={templateId || ""} onValueChange={setTemplateId}>
                <SelectTrigger data-testid="wizard-template-select"><SelectValue placeholder={t("prod.selectTemplate")} /></SelectTrigger>
                <SelectContent>
                  {templates.map((t2) => <SelectItem key={t2.id} value={t2.id}>{t2.name}{t2.is_starter ? ` ${t("prod.starterTag")}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              {templates.length === 0 && <p className="text-xs text-muted-foreground">{t("prod.noTemplates", { product: td(productType) })}</p>}
            </div>

            {template && (
              <div className="space-y-3">
                {template.fields.map((f) => {
                  const st = fieldStatus(f);
                  return (
                    <div key={f.id} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label>
                          {f.name} {f.required && <span className="text-[hsl(var(--danger))]">*</span>}
                          {(f.min != null || f.max != null) && (
                            <span className="ml-2 font-mono text-xs text-muted-foreground">
                              [{f.min != null ? f.min : "–"} – {f.max != null ? f.max : "–"}]
                            </span>
                          )}
                        </Label>
                        {st === "bad" && <StatusBadge status="bad" />}
                        {st === "ok" && <StatusBadge status="ok" />}
                      </div>
                      {renderQcInput(f, qcValues[f.id], (v) => setQcValues((q) => ({ ...q, [f.id]: v })), st, t)}
                      {st === "bad" && f.type === "select" && (
                        <div className="space-y-1 rounded-lg border border-[hsl(var(--danger)/0.4)] bg-[hsl(var(--danger)/0.05)] p-2.5">
                          <Label className="text-xs font-medium text-[hsl(var(--danger))]">
                            {t("prod.correctiveAction")} <span aria-hidden="true">*</span>
                          </Label>
                          <Textarea rows={2} value={qcCorrective[f.id] || ""}
                            onChange={(e) => setQcCorrective((c) => ({ ...c, [f.id]: e.target.value }))}
                            placeholder={t("prod.correctivePh")}
                            data-testid={`qc-corrective-${f.id}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex justify-between border-t border-border pt-3">
              <Button variant="outline" onClick={() => setStep("milk")}>{t("common.back")}</Button>
              <Button onClick={() => setStep("review")} data-testid="wizard-next-qc">{t("prod.nextReview")}</Button>
            </div>
          </TabsContent>

          {/* Step 4 */}
          <TabsContent value="review" className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info label={t("prod.product")} value={td(productType)} />
              <Info label={t("prod.operator")} value={operator || "-"} />
              <Info label={t("prod.totalMilk")} value={`${fmt(totalMilk)} L`} />
              <Info label={t("prod.qcTemplate")} value={template?.name || t("common.none")} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>{t("prod.targetFat")}</Label>
                <Input type="number" step="0.01" value={targetFat} onChange={(e) => setTargetFat(e.target.value)} className="text-right font-mono" data-testid="wizard-targetfat-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("common.notes")}</Label>
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="wizard-notes-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("prod.yield")}</Label>
              <YieldBreakdownEditor lines={yieldLines} onChange={setYieldLines} packagings={packagings} isFreezer={freezerSet.has(productType)} />
            </div>
            <ByproductEditor value={byproducts} onChange={setByproducts} productType={productType} />
            <div className="flex justify-between border-t border-border pt-3">
              <Button variant="outline" onClick={() => setStep("qc")}>{t("common.back")}</Button>
              <Button onClick={create} disabled={saving} data-testid="wizard-create-button">
                {saving ? t("prod.creating") : t("prod.createBtn")}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function renderQcInput(f, value, onChange, status, t) {
  const ring = status === "bad" ? "border-[hsl(var(--danger))] bg-[hsl(var(--qc-bad-bg))]" : "";
  if (f.type === "boolean") {
    return (
      <div className="flex items-center gap-2">
        <Switch checked={!!value} onCheckedChange={onChange} data-testid={`qc-input-${f.id}`} />
        <span className="text-sm text-muted-foreground">{value ? (t ? t("common.yes") : "Yes") : (t ? t("common.no") : "No")}</span>
      </div>
    );
  }
  if (f.type === "select") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={`qc-input-${f.id}`} className={ring}><SelectValue placeholder={t ? t("common.select") : "Select"} /></SelectTrigger>
        <SelectContent>
          {(f.options || []).map((o) => (
            <SelectItem key={o} value={o}>
              {o}{(f.fail_options || []).includes(o) && t ? ` · ${t("tpl.optionFail")}` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (f.type === "text") {
    return <Textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`qc-input-${f.id}`} />;
  }
  return (
    <div className="relative">
      <Input
        type="number"
        step="0.01"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={`pr-12 text-right font-mono tabular-nums ${ring}`}
        data-testid={`qc-input-${f.id}`}
      />
      {f.unit && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{f.unit}</span>}
    </div>
  );
}

const Info = ({ label, value }) => (
  <div className="rounded-lg border border-border p-3">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="font-medium">{value}</div>
  </div>
);

// Reusable by-products editor (used in wizard review + detail edit)
function ByproductEditor({ value, onChange, productType }) {
  const { t } = useT();
  const { all } = useProductTypes();
  const definedSubs = useMemo(() => {
    const pt = (all || []).find((p) => p.name === productType);
    return pt?.subproducts || [];
  }, [all, productType]);
  const list = value || [];
  const newRid = () => (crypto?.randomUUID?.() || `bp${Date.now()}${Math.random().toString(36).slice(2, 7)}`);
  const add = (name = "", unit = "kg") => onChange([...list, { _rid: newRid(), name, yield_kg: "", unit }]);
  const addSubproduct = (name) => {
    if (!name) return;
    if (list.some((b) => (b.name || "").toLowerCase() === name.toLowerCase())) return;
    const def = definedSubs.find((s) => s.name === name);
    add(name, def?.unit || "kg");
  };
  const update = (idx, key, v) => onChange(list.map((b, i) => (i === idx ? { ...b, [key]: v } : b)));
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  const availableSubs = definedSubs.filter((s) => !list.some((b) => (b.name || "").toLowerCase() === s.name.toLowerCase()));
  return (
    <div className="space-y-2 rounded-lg border border-border p-3" data-testid="byproducts-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label className="text-sm">{t("prod.byproductsOptional")}</Label>
        <div className="flex items-center gap-2">
          {availableSubs.length > 0 && (
            <Select value="" onValueChange={addSubproduct}>
              <SelectTrigger className="h-8 w-[200px] text-sm" data-testid="byproduct-subproduct-select">
                <SelectValue placeholder={t("prod.subproductPick")} />
              </SelectTrigger>
              <SelectContent>
                {availableSubs.map((s) => (
                  <SelectItem key={s.name} value={s.name} data-testid={`byproduct-subproduct-option-${s.name}`}>
                    {s.name} <span className="text-muted-foreground">· {s.unit === "l" ? "L" : "kg"}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={() => add("")} data-testid="byproduct-add-button">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> {t("prod.addByproduct")}
          </Button>
        </div>
      </div>
      {list.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {definedSubs.length === 0 && productType ? t("prod.subproductManageHint") : t("prod.byproductHint")}
        </p>
      ) : (
        list.map((b, idx) => (
          <div key={b._rid || `bp-${idx}`} className="flex items-center gap-2">
            <Input
              placeholder={t("prod.byproductNamePlaceholder")}
              value={b.name}
              onChange={(e) => update(idx, "name", e.target.value)}
              data-testid={`byproduct-name-${idx}`}
            />
            <div className="relative w-32 shrink-0">
              <Input
                type="number"
                placeholder="0"
                value={b.yield_kg ?? ""}
                onChange={(e) => update(idx, "yield_kg", e.target.value)}
                className="pr-8 text-right font-mono"
                data-testid={`byproduct-yield-${idx}`}
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{b.unit === "l" ? "L" : "kg"}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={() => remove(idx)} data-testid={`byproduct-remove-${idx}`}>
              <Trash2 className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        ))
      )}
    </div>
  );
}


// Subproduct inputs (raw material drawn from available by-product lots).
function SubproductInputEditor({ value, onChange, productType }) {
  const { t } = useT();
  const { all } = useProductTypes();
  const inputNames = useMemo(() => {
    const pt = (all || []).find((p) => p.name === productType);
    return pt?.input_subproducts || [];
  }, [all, productType]);
  const [lots, setLots] = useState([]);
  const list = value || [];

  useEffect(() => {
    if (!inputNames.length) { setLots([]); return undefined; }
    let active = true;
    getSubproductLots({ names: inputNames, availableOnly: true })
      .then((d) => { if (active) setLots(d || []); })
      .catch(() => { if (active) setLots([]); });
    return () => { active = false; };
  }, [inputNames]);

  const unitOf = (u) => (u === "l" ? "L" : "kg");
  const lotById = (id) => lots.find((l) => l.id === id);
  const addLot = (lotId) => {
    const lot = lotById(lotId);
    if (!lot || list.some((r) => r.lot_id === lotId)) return;
    onChange([...list, {
      lot_id: lot.id, name: lot.name, unit: lot.unit, qty: "",
      remaining: lot.remaining, source_production_code: lot.source_production_code,
    }]);
  };
  const updateQty = (lotId, qty) => onChange(list.map((r) => (r.lot_id === lotId ? { ...r, qty } : r)));
  const remove = (lotId) => onChange(list.filter((r) => r.lot_id !== lotId));
  const available = lots.filter((l) => !list.some((r) => r.lot_id === l.id));
  const hasOver = list.some((r) => Number(r.qty) > Number(r.remaining) + 1e-9);

  if (!inputNames.length) {
    return null;
  }

  return (
    <div className="space-y-2 rounded-lg border border-border p-3" data-testid="subproduct-inputs-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label className="text-sm">{t("prod.spInputsTitle")}</Label>
        {available.length > 0 && (
          <Select value="" onValueChange={addLot}>
            <SelectTrigger className="h-8 w-[280px] text-sm" data-testid="subproduct-lot-select">
              <SelectValue placeholder={t("prod.spPickLot")} />
            </SelectTrigger>
            <SelectContent>
              {available.map((l) => (
                <SelectItem key={l.id} value={l.id} data-testid={`subproduct-lot-option-${l.id}`}>
                  {l.lot_code ? <span className="font-mono">{l.lot_code}</span> : null}
                  {l.lot_code ? " · " : ""}{l.name} · {fmt(l.remaining)} {unitOf(l.unit)} {t("prod.spFrom")} {l.source_production_code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {list.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {lots.length === 0 ? t("prod.spInputsNoStock", { names: inputNames.join(", ") }) : t("prod.spInputsHint")}
        </p>
      ) : (
        <>
          {list.map((r) => {
            const over = Number(r.qty) > Number(r.remaining) + 1e-9;
            return (
              <div key={r.lot_id} className="flex items-center gap-2" data-testid={`subproduct-input-row-${r.lot_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{r.name}</div>
                  <div className="truncate text-[11px] text-muted-foreground">
                    {r.source_production_code} · {fmt(r.remaining)} {unitOf(r.unit)} {t("prod.spAvailable")}
                  </div>
                </div>
                <div className="relative w-32 shrink-0">
                  <Input
                    type="number" placeholder="0" value={r.qty ?? ""}
                    onChange={(e) => updateQty(r.lot_id, e.target.value)}
                    className={`pr-8 text-right font-mono ${over ? "border-[hsl(var(--warning))]" : ""}`}
                    data-testid={`subproduct-input-qty-${r.lot_id}`}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{unitOf(r.unit)}</span>
                </div>
                <Button variant="ghost" size="icon" onClick={() => remove(r.lot_id)} data-testid={`subproduct-input-remove-${r.lot_id}`}>
                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            );
          })}
          {hasOver && (
            <p className="flex items-center gap-1.5 text-xs text-[hsl(var(--warning))]" data-testid="subproduct-input-warning">
              <AlertTriangle className="h-3.5 w-3.5" /> {t("prod.spInsufficient")}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// Raw materials (rennet, cultures, salt, additives) consumed in a production batch.
// `consumedByLot` maps lot_id -> qty already consumed by THIS production (edit mode), so the
// available amount is shown correctly (the backend restores then re-consumes on update).
function RawMaterialInputEditor({ value, onChange, consumedByLot = {} }) {
  const { t } = useT();
  const [lots, setLots] = useState([]);
  const list = value || [];

  useEffect(() => {
    let active = true;
    getRawMaterialLots({})
      .then((d) => { if (active) setLots(d || []); })
      .catch(() => { if (active) setLots([]); });
    return () => { active = false; };
  }, []);

  const lotById = (id) => lots.find((l) => l.id === id);
  // Effective available = current remaining + what this production already took from the lot.
  const effRemaining = (lot) => Number(lot?.remaining || 0) + Number(consumedByLot[lot?.id] || 0);
  const remainingFor = (r) => {
    const lot = lotById(r.lot_id);
    return lot ? effRemaining(lot) : Number(r.remaining || 0);
  };
  const addLot = (lotId) => {
    const lot = lotById(lotId);
    if (!lot || list.some((r) => r.lot_id === lotId)) return;
    onChange([...list, {
      lot_id: lot.id, type_name: lot.type_name, lot_code: lot.lot_code,
      supplier_lot_no: lot.supplier_lot_no || "", supplier_name: lot.supplier_name || "",
      unit: lot.unit, qty: "", remaining: effRemaining(lot),
    }]);
  };
  const updateQty = (lotId, qty) => onChange(list.map((r) => (r.lot_id === lotId ? { ...r, qty } : r)));
  const remove = (lotId) => onChange(list.filter((r) => r.lot_id !== lotId));
  const available = lots.filter((l) => effRemaining(l) > 0 && !list.some((r) => r.lot_id === l.id));
  const hasOver = list.some((r) => Number(r.qty) > remainingFor(r) + 1e-9);

  return (
    <div className="space-y-2 rounded-lg border border-border p-3" data-testid="rm-inputs-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Label className="text-sm">{t("rawMaterials.prodSectionTitle")}</Label>
          <p className="text-[11px] text-muted-foreground">{t("rawMaterials.prodSectionHint")}</p>
        </div>
        {available.length > 0 && (
          <Select value="" onValueChange={addLot}>
            <SelectTrigger className="h-8 w-[320px] text-sm" data-testid="rm-input-lot-select">
              <SelectValue placeholder={t("rawMaterials.addLine")} />
            </SelectTrigger>
            <SelectContent>
              {available.map((l) => (
                <SelectItem key={l.id} value={l.id} data-testid={`rm-input-lot-option-${l.id}`}>
                  <span className="font-mono">{l.lot_code}</span> · {l.type_name}
                  {l.supplier_lot_no ? <span className="text-muted-foreground"> · {t("rawMaterials.supplierLot")}: {l.supplier_lot_no}</span> : null}
                  <span className="text-muted-foreground"> · {fmt(effRemaining(l))} {l.unit}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {list.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {lots.length === 0 ? t("rawMaterials.lotsEmpty") : t("rawMaterials.prodSectionHint")}
        </p>
      ) : (
        <>
          {list.map((r) => {
            const rem = remainingFor(r);
            const over = Number(r.qty) > rem + 1e-9;
            return (
              <div key={r.lot_id} className="flex items-center gap-2" data-testid={`rm-input-row-${r.lot_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{r.type_name}</div>
                  <div className="truncate text-[11px] text-muted-foreground">
                    <span className="font-mono">{r.lot_code}</span>
                    {r.supplier_lot_no ? <span> · {t("rawMaterials.supplierLot")}: <span className="font-mono">{r.supplier_lot_no}</span></span> : null}
                    {" · "}{fmt(rem)} {r.unit} {t("prod.spAvailable")}
                  </div>
                </div>
                <div className="relative w-32 shrink-0">
                  <Input
                    type="number" placeholder="0" value={r.qty ?? ""}
                    onChange={(e) => updateQty(r.lot_id, e.target.value)}
                    className={`pr-8 text-right font-mono ${over ? "border-[hsl(var(--warning))]" : ""}`}
                    data-testid={`rm-input-qty-${r.lot_id}`}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{r.unit}</span>
                </div>
                <Button variant="ghost" size="icon" onClick={() => remove(r.lot_id)} data-testid={`rm-input-remove-${r.lot_id}`}>
                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            );
          })}
          {hasOver && (
            <p className="flex items-center gap-1.5 text-xs text-[hsl(var(--warning))]" data-testid="rm-input-warning">
              <AlertTriangle className="h-3.5 w-3.5" /> {t("prod.spInsufficient")}
            </p>
          )}
        </>
      )}
    </div>
  );
}




const yRid = () => (crypto?.randomUUID?.() || `y${Date.now()}${Math.random().toString(36).slice(2, 7)}`);
const emptyYieldLines = () => [{ _rid: yRid(), unit: "kg", qty: "", storage_state: "frozen" }];
const yieldLinesFromDetail = (d) => {
  if (d?.yield_breakdown?.length) return d.yield_breakdown.map((b) => ({ _rid: yRid(), unit: b.unit, qty: b.qty, storage_state: b.storage_state || "frozen" }));
  if (d?.yield_qty) return [{ _rid: yRid(), unit: d.yield_unit || "kg", qty: d.yield_qty, storage_state: "frozen" }];
  return emptyYieldLines();
};
const cleanYieldLines = (lines) =>
  (lines || []).filter((l) => l.unit && Number(l.qty) > 0).map((l) => ({ unit: l.unit, qty: Number(l.qty), storage_state: l.storage_state || "frozen" }));

// Multi-packaging yield editor: split a batch's output across several package sizes.
// Everything is summed into the product's bulk kg pool (total kg shown live).
function YieldBreakdownEditor({ lines, onChange, packagings, compact, isFreezer }) {
  const { t } = useT();
  const list = lines || [];
  // Non-kg packaging options for the per-line unit dropdown — memoized once instead of per line.
  const pkgOptions = useMemo(() => packagings.filter((p) => p.label !== "kg"), [packagings]);
  const netOf = (unit) => (unit === "kg" ? 1 : Number((packagings.find((p) => p.label === unit) || {}).net_kg) || 0);
  const totalKg = list.reduce((s, l) => s + (Number(l.qty) || 0) * netOf(l.unit), 0);
  const hasNoNet = list.some((l) => Number(l.qty) > 0 && netOf(l.unit) <= 0);
  const setLine = (rid_, patch) => onChange(list.map((l) => (l._rid === rid_ ? { ...l, ...patch } : l)));
  const addLine = () => onChange([...list, { _rid: yRid(), unit: "kg", qty: "", storage_state: "frozen" }]);
  const rmLine = (rid_) => onChange(list.filter((l) => l._rid !== rid_));
  const h = compact ? "h-8" : "";
  return (
    <div className="space-y-2" data-testid="yield-breakdown-editor">
      {list.map((l, i) => (
        <div key={l._rid} className="flex items-center gap-2">
          <Input
            type="number"
            value={l.qty}
            onChange={(e) => setLine(l._rid, { qty: e.target.value })}
            className={`text-right font-mono ${h}`}
            placeholder={t("common.qty")}
            data-testid={`yield-line-qty-${i}`}
          />
          <Select value={l.unit} onValueChange={(v) => setLine(l._rid, { unit: v })}>
            <SelectTrigger className={`w-36 shrink-0 ${h}`} data-testid={`yield-line-unit-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="kg">kg</SelectItem>
              {pkgOptions.map((p) => (
                <SelectItem key={p.id} value={p.label}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isFreezer && (
            <Select value={l.storage_state || "frozen"} onValueChange={(v) => setLine(l._rid, { storage_state: v })}>
              <SelectTrigger className={`w-40 shrink-0 ${h}`} data-testid={`yield-line-storage-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="frozen">
                  <span className="flex items-center gap-1.5"><Snowflake className="h-3.5 w-3.5" /> {t("stock.frozen")}</span>
                </SelectItem>
                <SelectItem value="refrigerated">
                  <span className="flex items-center gap-1.5"><ThermometerSnowflake className="h-3.5 w-3.5" /> {t("stock.refrigerated")}</span>
                </SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button
            type="button" variant="ghost" size="icon" className={`shrink-0 ${h}`}
            onClick={() => rmLine(l._rid)} disabled={list.length <= 1}
            data-testid={`yield-line-remove-${i}`}
          >
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      ))}
      <div className="flex items-center justify-between gap-2">
        <Button type="button" variant="outline" size="sm" onClick={addLine} data-testid="yield-add-line">
          <Plus className="mr-1.5 h-3.5 w-3.5" /> {t("prod.addPackaging")}
        </Button>
        <span className="font-mono text-xs text-muted-foreground" data-testid="yield-total-kg">
          {t("prod.yieldTotalKg", { kg: fmt(totalKg, 2) })}
        </span>
      </div>
      {isFreezer && <p className="text-[11px] text-muted-foreground">{t("prod.perLineStorageHint")}</p>}
      {hasNoNet && <p className="text-[11px] text-[hsl(var(--danger))]">{t("prod.yieldSomeNoNet")}</p>}
    </div>
  );
}

// ---------------- Specific consumption (Consum specific) ----------------
// Admin/manager-only KPI: milk translated to kg (configurable density), minus the selected
// deducted subproducts (e.g. skimmed cream), divided by the batch yield →
// "X kg milk / kg product". Deduction defaults come from the product's subproduct config
// (Products page) and can be adjusted per production (persisted on the doc).
function SpecificConsumptionPanel({ detail, onUpdated }) {
  const { t, td } = useT();
  const { all: allProducts } = useProductTypes();
  const [density, setDensity] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getCompanySettings()
      .then((cs) => setDensity(Number(cs?.milk_density_kg_per_l) > 0 ? Number(cs.milk_density_kg_per_l) : 1.03))
      .catch(() => setDensity(1.03));
  }, []);

  const milkL = Number(detail.total_milk_l || 0);
  const yieldKg = Number(detail.yield_kg || 0);

  // By-products of THIS batch that have a recorded quantity — the deduction candidates.
  const candidates = useMemo(
    () => (detail.byproducts || []).filter((b) => b?.name && Number(b.yield_kg) > 0),
    [detail.byproducts],
  );

  // Effective selection: per-production override if stored, else the product's admin defaults.
  const selected = useMemo(() => {
    if (Array.isArray(detail.deduct_subproducts)) return detail.deduct_subproducts;
    const product = (allProducts || []).find((p) => p.name === detail.product_type);
    const defaults = new Set((product?.subproducts || []).filter((s) => s?.deduct_from_milk).map((s) => s.name));
    return candidates.filter((b) => defaults.has(b.name)).map((b) => b.name);
  }, [detail.deduct_subproducts, detail.product_type, allProducts, candidates]);

  const toggle = async (name) => {
    const next = selected.includes(name) ? selected.filter((n) => n !== name) : [...selected, name];
    setSaving(true);
    try {
      await updateProduction(detail.id, { deduct_subproducts: next });
      onUpdated && (await onUpdated(detail.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("prod.updateFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (!(milkL > 0) || density == null) return null;

  const milkKg = milkL * density;
  const dedKg = candidates.filter((b) => selected.includes(b.name)).reduce((s, b) => s + Number(b.yield_kg || 0), 0);
  const netKg = milkKg - dedKg;
  const sc = yieldKg > 0 && netKg > 0 ? netKg / yieldKg : null;
  // Per-product target (kg net milk / kg product) — batches above it are flagged.
  const target = Number(detail.target_specific_consumption) > 0 ? Number(detail.target_specific_consumption) : null;
  const overTarget = sc != null && target != null && sc > target;

  return (
    <div className="mt-5 rounded-lg border border-border bg-secondary/30 p-3" data-testid="specific-consumption-panel">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold">
        <Scale className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("prod.scTitle")}
      </h3>
      <p className="mt-1 text-[11px] text-muted-foreground">{t("prod.scHint")}</p>

      <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" data-testid="sc-milk-line">
        <span className="text-muted-foreground">
          {t("prod.scMilk")}: <span className="font-mono font-medium text-foreground">{fmt(milkL)} L → {fmt(milkKg, 2)} kg</span>
          <span className="ml-1 text-[10px]">(× {density})</span>
        </span>
        <span className="text-muted-foreground">
          {t("prod.scNetMilk")}: <span className="font-mono font-medium text-foreground" data-testid="sc-net-milk">{fmt(netKg, 2)} kg</span>
        </span>
        <span className="text-muted-foreground">
          {t("prod.yield")}: <span className="font-mono font-medium text-foreground">{fmt(yieldKg)} kg</span>
        </span>
      </div>

      <div className="mt-2.5">
        <div className="text-[11px] font-medium text-muted-foreground">{t("prod.scDeducted")}</div>
        {candidates.length === 0 ? (
          <p className="mt-1 text-[11px] text-muted-foreground">{t("prod.scNoSubs")}</p>
        ) : (
          <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="sc-deduct-chips">
            {candidates.map((b) => {
              const on = selected.includes(b.name);
              return (
                <button key={b.name} type="button" onClick={() => toggle(b.name)} disabled={saving}
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${on
                    ? "border-transparent bg-[hsl(var(--brand))] text-white"
                    : "border-border bg-background text-muted-foreground hover:bg-secondary"}`}
                  title={on ? t("prod.scChipOn") : t("prod.scChipOff")}
                  data-testid={`sc-deduct-chip-${b.name}`}>
                  <Droplets className="h-3 w-3" /> {td(b.name)} · {fmt(b.yield_kg)} kg
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-1" data-testid="sc-result">
        <span className="text-xs text-muted-foreground">{t("prod.scResult")}:</span>
        {sc != null ? (
          <>
            <span className={`font-mono text-base font-semibold ${overTarget ? "text-[hsl(var(--danger))]" : "text-[hsl(var(--brand))]"}`}
              data-testid="sc-result-value">
              {fmt(sc, 2)} {t("prod.scUnit")}
            </span>
            {target != null && (
              overTarget ? (
                <Badge className="gap-1 border-transparent bg-[hsl(var(--danger)/0.12)] text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger)/0.12)]" data-testid="sc-over-target-badge">
                  <AlertTriangle className="h-3 w-3" /> {t("prod.scOverTarget", { target: fmt(target, 2) })}
                </Badge>
              ) : (
                <span className="text-[11px] text-muted-foreground" data-testid="sc-target-label">
                  {t("prod.scTarget")}: {fmt(target, 2)} {t("prod.scUnit")}
                </span>
              )
            )}
          </>
        ) : (
          <span className="text-xs text-muted-foreground" data-testid="sc-result-missing">
            {yieldKg > 0 ? t("prod.scNoMilkLeft") : t("prod.scNoYield")}
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------- Detail drawer ----------------
const qcUid = () => "f" + Math.random().toString(36).slice(2, 9);
const QC_TYPES = ["number", "text", "temperature", "ph", "time", "boolean", "select"];
function ProductionDetail({ detail, statuses, onClose, onUpdated }) {
  const { can } = useAuth();
  const { t, td } = useT();
  const canEditStructure = can("admin", "manager", "qa", "operator");
  const canDelete = can("admin", "manager");
  const canEditLotNo = can("admin", "manager");
  const canEditMilk = can("admin", "manager", "qa", "operator");
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [packagings, setPackagings] = useState([]);
  const [isFreezerProduct, setIsFreezerProduct] = useState(false);
  const { all: allProductTypes } = useProductTypes();
  // Sellable sub-variants of THIS production's product (e.g. "Fresh Telemea" under "Telemea").
  const sellableVariants = useMemo(
    () => (allProductTypes || []).filter((pt) => pt.parent_product === detail?.product_type),
    [allProductTypes, detail?.product_type],
  );
  const [variantProduct, setVariantProduct] = useState("");
  const [variantLines, setVariantLines] = useState([{ _rid: yRid(), unit: "kg", qty: "" }]);
  const [variantBusy, setVariantBusy] = useState(false);

  const netOfPkg = (u) => (u === "kg" ? 1 : Number((packagings.find((p) => p.label === u) || {}).net_kg) || 0);
  const pkgOptions = useMemo(() => packagings.filter((p) => p.label !== "kg"), [packagings]);
  const variantTotalKg = variantLines.reduce((s, l) => s + (Number(l.qty) || 0) * netOfPkg(l.unit), 0);
  const variantHasNoNet = variantLines.some((l) => Number(l.qty) > 0 && netOfPkg(l.unit) <= 0);
  const allocations = detail?.variant_allocations || [];
  const allocatedKg = allocations.reduce((s, a) => s + (Number(a.total_kg) || 0), 0);

  const setVLine = (rid_, patch) => setVariantLines((ls) => ls.map((l) => (l._rid === rid_ ? { ...l, ...patch } : l)));
  const addVLine = () => setVariantLines((ls) => [...ls, { _rid: yRid(), unit: "kg", qty: "" }]);
  const rmVLine = (rid_) => setVariantLines((ls) => (ls.length <= 1 ? ls : ls.filter((l) => l._rid !== rid_)));

  const submitVariantAllocation = async () => {
    const lines = variantLines
      .filter((l) => l.unit && Number(l.qty) > 0)
      .map((l) => ({ unit: l.unit, qty: Number(l.qty) }));
    if (!variantProduct || lines.length === 0) return;
    setVariantBusy(true);
    try {
      await addVariantAllocation(detail.id, { product_type: variantProduct, lines });
      toast.success(t("prod.variantAllocAdded", { name: td(variantProduct) }));
      setVariantProduct("");
      setVariantLines([{ _rid: yRid(), unit: "kg", qty: "" }]);
      if (onUpdated) await onUpdated(detail.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("prod.variantStockError"));
    } finally {
      setVariantBusy(false);
    }
  };

  const removeVariantAllocation = async (allocId) => {
    try {
      await deleteVariantAllocation(detail.id, allocId);
      toast.success(t("prod.variantAllocRemoved"));
      if (onUpdated) await onUpdated(detail.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("prod.variantAllocRemoveError"));
    }
  };

  // Named (non-empty) byproducts — memoized; used both for the count check and the list render.
  const namedByproducts = useMemo(
    () => (detail?.byproducts || []).filter((b) => b && b.name),
    [detail?.byproducts],
  );

  useEffect(() => {
    getPackagingOptions().then((ps) => setPackagings((ps || []).filter((p) => p.active !== false)));
  }, []);
  useEffect(() => {
    getProductSettings().then((rows) => {
      const row = (rows || []).find((r) => r.product_type === detail?.product_type);
      setIsFreezerProduct(!!row?.freezer_storage);
    });
  }, [detail?.product_type]);

  const doDelete = async () => {
    setSaving(true);
    try {
      const res = await deleteProduction(detail.id);
      toast.success(`${t("prod.prodDeleted")}${res?.restored?.length ? `: ${res.restored.join(", ")}` : ""}`);
      setConfirmDel(false);
      onClose();
      if (onUpdated) await onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("prod.failedDelete"));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    (async () => {
      setEdit(false);
      setDraft(null);
      setConfirmDel(false);
    })();
  }, [detail?.id]);

  if (!detail) return null;
  const tpl = detail.template_snapshot;

  const startEdit = () => {
    setDraft({
      fields: tpl ? tpl.fields.map((f) => ({ ...f })) : [],
      qc_values: { ...(detail.qc_values || {}) },
      qc_corrective: { ...(detail.qc_corrective || {}) },
      yield_lines: yieldLinesFromDetail(detail),
      date: (detail.date || "").slice(0, 10),
      production_code: detail.production_code || "",
      byproducts: (detail.byproducts || []).map((b) => ({ _rid: (crypto?.randomUUID?.() || `bp${Math.random().toString(36).slice(2, 9)}`), name: b.name, yield_kg: b.yield_kg ?? "" })),
      status: detail.status || statuses?.[0]?.label || "In progress",
      notes: detail.notes || "",
      milk_inputs: (detail.milk_inputs || []).map((i) => ({ milk_batch_id: i.milk_batch_id, batch_code: i.batch_code, qty_l: i.qty_l ?? "" })),
      rm_inputs: (detail.raw_material_inputs || []).map((ri) => ({
        lot_id: ri.lot_id, type_name: ri.type_name, lot_code: ri.lot_code,
        supplier_lot_no: ri.supplier_lot_no || "", supplier_name: ri.supplier_name || "",
        unit: ri.unit, qty: ri.qty ?? "",
      })),
    });
    setEdit(true);
  };

  const setVal = (id, v) => setDraft((d) => ({ ...d, qc_values: { ...d.qc_values, [id]: v } }));
  const setCorr = (id, v) => setDraft((d) => ({ ...d, qc_corrective: { ...(d.qc_corrective || {}), [id]: v } }));
  const removeField = (id) =>
    setDraft((d) => ({ ...d, fields: d.fields.filter((f) => f.id !== id) }));
  const addField = (field) => {
    setDraft((d) => ({ ...d, fields: [...d.fields, field] }));
    setAddOpen(false);
  };

  const fieldStatus = (f, v) => {
    if (v === undefined || v === "") return null;
    if (f.type === "select") return (f.fail_options || []).includes(v) ? "bad" : "ok";
    if (!["number", "temperature", "ph", "time"].includes(f.type)) return null;
    const n = Number(v);
    if (Number.isNaN(n)) return null;
    return (f.min != null && n < f.min) || (f.max != null && n > f.max) ? "bad" : "ok";
  };

  const save = async () => {
    // Fail-option discipline: corrective note required for each non-conforming selection.
    const missingCorr = (draft.fields || []).find((f) =>
      f.type === "select" && (f.fail_options || []).includes(draft.qc_values[f.id]) && !((draft.qc_corrective || {})[f.id] || "").trim());
    if (missingCorr) {
      toast.error(t("prod.correctiveRequired", { field: missingCorr.name }));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        qc_values: draft.qc_values,
        qc_corrective: draft.qc_corrective || {},
        yield_breakdown: cleanYieldLines(draft.yield_lines),
        byproducts: (draft.byproducts || [])
          .filter((b) => b.name.trim())
          .map((b) => ({ name: b.name.trim(), yield_kg: b.yield_kg === "" ? null : Number(b.yield_kg) })),
        status: draft.status,
        notes: draft.notes,
        date: draft.date ? new Date(draft.date).toISOString() : undefined,
      };
      if (canEditLotNo) {
        const newCode = (draft.production_code || "").trim();
        if (newCode && newCode !== detail.production_code) payload.production_code = newCode;
      }
      if (tpl) payload.template_snapshot = { ...tpl, fields: draft.fields };
      // Raw materials: send the full (replacement) list; backend restores the old ones then
      // re-consumes these. Only include when it actually changed to avoid needless churn.
      if (Array.isArray(draft.rm_inputs)) {
        const rmi = draft.rm_inputs
          .filter((r) => r.lot_id && r.qty !== "" && Number(r.qty) > 0)
          .map((r) => ({ lot_id: r.lot_id, qty: Number(r.qty) }));
        const origRmi = (detail.raw_material_inputs || []).map((r) => ({ lot_id: r.lot_id, qty: Number(r.qty) }));
        const changed = JSON.stringify(rmi.map((r) => [r.lot_id, r.qty])) !==
          JSON.stringify(origRmi.map((r) => [r.lot_id, r.qty]));
        if (changed) payload.raw_material_inputs = rmi;
      }
      if (canEditMilk && Array.isArray(draft.milk_inputs)) {
        const mi = draft.milk_inputs
          .filter((i) => i.milk_batch_id && i.qty_l !== "" && Number(i.qty_l) > 0)
          .map((i) => ({ milk_batch_id: i.milk_batch_id, qty_l: Number(i.qty_l) }));
        const changed = JSON.stringify(mi.map((i) => [i.milk_batch_id, i.qty_l])) !==
          JSON.stringify((detail.milk_inputs || []).map((i) => [i.milk_batch_id, Number(i.qty_l)]));
        if (mi.length && changed) payload.milk_inputs = mi;
      }
      await updateProduction(detail.id, payload);
      toast.success(t("prod.qcUpdated"));
      setEdit(false);
      onUpdated && (await onUpdated(detail.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("prod.updateFailed"));
    } finally {
      setSaving(false);
    }
  };

  const activeFields = edit ? draft.fields : tpl?.fields || [];

  return (
    <Drawer open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent className="max-h-[90vh]">
        <DrawerHeader className="flex flex-row items-center justify-between">
          <DrawerTitle className="flex items-center gap-2">
            {edit && canEditLotNo ? (
              <div className="flex items-center gap-1.5" data-testid="production-lot-edit-wrap" title={t("prod.lotNumberHint")}>
                <span className="text-xs text-muted-foreground">{t("prod.lotNumber")}</span>
                <Input
                  value={draft?.production_code ?? ""}
                  onChange={(e) => setDraft((d) => ({ ...d, production_code: e.target.value }))}
                  className="h-8 w-40 font-mono text-sm"
                  maxLength={40}
                  data-testid="production-lot-input"
                />
              </div>
            ) : (
              <span className="font-mono text-sm">{detail.production_code}</span>
            )}
            <Badge variant="secondary">{td(detail.product_type)}</Badge>
          </DrawerTitle>
          <div className="flex items-center gap-2">
            {!edit ? (
              <>
                <Button variant="outline" size="sm" onClick={startEdit} data-testid="production-edit-button">
                  <Pencil className="mr-1.5 h-4 w-4" /> {t("prod.editQc")}
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadPdf(`/productions/${detail.id}/report`, `production_${detail.production_code}.pdf`).catch(() => toast.error(t("prod.reportFailed")))} data-testid="production-report-button">
                  <FileText className="mr-1.5 h-4 w-4" /> PDF
                </Button>
                {canDelete && (
                  <Button variant="outline" size="sm" onClick={() => setConfirmDel(true)} data-testid="production-delete-button"
                    className="text-[hsl(var(--danger))] hover:bg-[hsl(var(--qc-bad-bg))]">
                    <Trash2 className="mr-1.5 h-4 w-4" /> {t("common.delete")}
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => setEdit(false)} data-testid="production-edit-cancel">
                  <X className="mr-1.5 h-4 w-4" /> {t("common.cancel")}
                </Button>
                <Button size="sm" onClick={save} disabled={saving} data-testid="production-edit-save">
                  {saving ? t("common.saving") : t("prod.saveQc")}
                </Button>
              </>
            )}
          </div>
        </DrawerHeader>
        <div className="overflow-y-auto thin-scroll px-4 pb-8">
          {confirmDel && (
            <div className="mb-4 rounded-lg border border-[hsl(var(--danger))] bg-[hsl(var(--qc-bad-bg))] p-4" data-testid="production-delete-confirm-banner">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 text-[hsl(var(--danger))]" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-foreground">{t("prod.deleteProdTitle", { code: detail.production_code })}</div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("prod.deleteProdBody")}
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setConfirmDel(false)} disabled={saving} data-testid="production-delete-cancel">{t("common.cancel")}</Button>
                    <Button size="sm" onClick={doDelete} disabled={saving}
                      className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90" data-testid="production-delete-confirm">
                      {saving ? t("common.deleting") : t("prod.deleteRestoreMilk")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {(detail.qc_warnings || []).length > 0 && !edit && (
            <div className="mb-4 rounded-lg badge-bad p-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <AlertTriangle className="h-4 w-4" /> {t("prod.outOfRange", { n: detail.qc_warnings.length })}
              </div>
              <ul className="mt-1 space-y-0.5 text-xs">
                {detail.qc_warnings.map((w, i) => (
                  <li key={`${w.field}-${i}`} className="font-mono">
                    {w.field}: {typeof w.value === "number" ? fmt(w.value, 2) : w.value} ({w.issue})
                    {w.corrective && (
                      <span className="ml-1 font-sans not-italic text-foreground/80" data-testid={`qc-warning-corrective-${i}`}>
                        — {t("prod.correctiveAction")}: {w.corrective}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <Info label={t("prod.operator")} value={detail.operator_name || "-"} />
            <Info label={t("prod.totalMilk")} value={`${fmt(detail.total_milk_l)} L`} />
            {edit ? (
              <div className="space-y-1.5" data-testid="detail-date-edit">
                <div className="text-xs text-muted-foreground">{t("prod.manufactureDate")}</div>
                <Input
                  type="date"
                  value={draft.date || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
                  data-testid="production-date-input"
                  className="h-9"
                />
              </div>
            ) : (
              <Info label={t("prod.manufactureDate")} value={(detail.date || "").slice(0, 10) || "-"} />
            )}
            <div className="space-y-1.5" data-testid="detail-expiration">
              <div className="text-xs text-muted-foreground">{t("prod.expirationDate")}</div>
              {detail.expiration_date ? (
                <div className="inline-flex items-center gap-1.5 text-sm font-medium">
                  <CalendarClock className="h-3.5 w-3.5 text-[hsl(var(--brand))]" />
                  {detail.expiration_date}
                  {detail.shelf_life_days ? <span className="text-xs text-muted-foreground">({detail.shelf_life_days}d)</span> : null}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">{t("prod.shelfNotSet")}</div>
              )}
            </div>
            <div className="space-y-1.5" data-testid="detail-maturation">
              <div className="text-xs text-muted-foreground">{t("prod.coldRoomReady")}</div>
              {detail.maturation_ready_date ? (
                <div className="inline-flex items-center gap-1.5 text-sm font-medium">
                  <Snowflake className="h-3.5 w-3.5 text-[hsl(var(--brand))]" />
                  {detail.maturation_ready_date}
                  {detail.in_maturation && detail.maturation_days_left != null && (
                    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${detail.maturation_days_left <= 0 ? "badge-bad" : "bg-[hsl(var(--qc-warn-bg))] text-[hsl(var(--warning))]"}`}>
                      {detail.maturation_days_left <= 0 ? t("prod.ready") : t("prod.daysShort", { n: detail.maturation_days_left })}
                    </span>
                  )}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">{detail.maturation_days ? t("prod.notInMaturation") : t("prod.maturationNotSet")}</div>
              )}
            </div>
            {edit ? (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("prod.yield")}</Label>
                <YieldBreakdownEditor
                  lines={draft.yield_lines}
                  onChange={(ls) => setDraft((d) => ({ ...d, yield_lines: ls }))}
                  packagings={packagings}
                  isFreezer={isFreezerProduct}
                  compact
                />
              </div>
            ) : (
              <Info label={t("prod.yield")} value={
                detail.yield_breakdown?.length
                  ? `${detail.yield_breakdown.map((b) => `${fmt(b.qty)}×${b.unit}${isFreezerProduct && b.storage_state ? ` · ${b.storage_state === "refrigerated" ? t("stock.refrigerated") : t("stock.frozen")}` : ""}`).join(", ")} (${fmt(detail.yield_kg)} kg)`
                  : (detail.yield_qty != null && detail.yield_unit && detail.yield_unit !== "kg"
                    ? `${fmt(detail.yield_qty)} ${detail.yield_unit} (${fmt(detail.yield_kg)} kg)`
                    : (detail.yield_kg ? `${fmt(detail.yield_kg)} kg` : "-"))} />
            )}
            <div className="space-y-1.5" data-testid="detail-efficiency">
              <div className="text-xs text-muted-foreground">{t("prod.yieldEfficiency")}</div>
              {detail.target_yield_per_100l ? (
                detail.yield_efficiency_pct != null ? (
                  <div className="inline-flex items-center gap-1.5 text-sm font-medium">
                    <EfficiencyBadge pct={detail.yield_efficiency_pct} />
                    <span className="text-xs text-muted-foreground">{t("prod.vsTarget", { kg: fmt(detail.expected_yield_kg) })}</span>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">{t("prod.addYieldCompute")}</div>
                )
              ) : (
                <div className="text-sm text-muted-foreground">{t("prod.noTarget")}</div>
              )}
            </div>
            {edit ? (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("common.status")}</Label>
                <Select value={draft.status} onValueChange={(v) => setDraft((d) => ({ ...d, status: v }))}>
                  <SelectTrigger className="h-8" data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(statuses || []).map((s) => (
                      <SelectItem key={s.id} value={s.label}>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                          {s.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-1.5">
                <div className="text-xs text-muted-foreground">{t("common.status")}</div>
                <StatusPill label={detail.status} statuses={statuses} />
              </div>
            )}
          </div>

          {/* Specific consumption (milk kg − deducted subproducts) ÷ yield — admin/manager only.
              The total yield above stays visible to everyone. */}
          {canDelete && <SpecificConsumptionPanel detail={detail} onUpdated={onUpdated} />}

          {/* Sub-variant allocations — carved out of this production's main-product output
              (shown right next to the Yield). Works on any production, incl. older ones. */}
          {(sellableVariants.length > 0 || allocations.length > 0) && (
            <div className="mt-5 rounded-lg border border-border bg-secondary/30 p-3" data-testid="production-variants-panel">
              <h3 className="flex items-center gap-1.5 text-sm font-semibold">
                <Tag className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("prod.variantStockTitle")}
              </h3>
              <p className="mt-1 text-[11px] text-muted-foreground">{t("prod.variantStockHint")}</p>

              {/* Yield accounting: total yield vs kg carved to sub-variants */}
              <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" data-testid="variant-yield-summary">
                <span className="text-muted-foreground">{t("prod.yield")}: <span className="font-mono font-medium text-foreground">{fmt(detail.yield_kg)} kg</span></span>
                <span className="text-muted-foreground">{t("prod.variantAllocatedKg")}: <span className="font-mono font-medium text-[hsl(var(--brand))]" data-testid="variant-allocated-kg">{fmt(allocatedKg)} kg</span></span>
              </div>

              {/* Existing allocations */}
              {allocations.length > 0 && (
                <div className="mt-2.5 space-y-1.5" data-testid="variant-allocations-list">
                  {allocations.map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid={`variant-allocation-${a.id}`}>
                      <div className="min-w-0">
                        <span className="font-medium">{td(a.product_type)}</span>
                        <span className="ml-2 text-xs text-muted-foreground">
                          {(a.lines || []).map((ln) => `${fmt(ln.qty)} × ${ln.unit}`).join(", ")}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-mono text-xs">{fmt(a.total_kg)} kg</span>
                        {canDelete && (
                          <Button variant="ghost" size="icon" className="h-7 w-7"
                            onClick={() => removeVariantAllocation(a.id)}
                            title={t("prod.variantAllocRemove")}
                            data-testid={`variant-allocation-remove-${a.id}`}>
                            <Trash2 className="h-4 w-4 text-[hsl(var(--danger))]" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add a new allocation (packaging-based, like the main product yield) */}
              {!edit && canEditStructure && sellableVariants.length > 0 && (
                <div className="mt-3 border-t border-border pt-3" data-testid="variant-alloc-editor">
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-[180px] flex-1">
                      <Label className="text-xs">{t("prod.variantStockProduct")}</Label>
                      <Select value={variantProduct} onValueChange={setVariantProduct}>
                        <SelectTrigger className="mt-1 h-9 text-sm" data-testid="variant-stock-product">
                          <SelectValue placeholder={t("prod.variantStockPick")} />
                        </SelectTrigger>
                        <SelectContent>
                          {sellableVariants.map((v) => (
                            <SelectItem key={v.id} value={v.name} data-testid={`variant-stock-option-${v.id}`}>{td(v.name)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* packaging lines — same editor style as the main product yield */}
                  <div className="mt-2.5 space-y-2">
                    {variantLines.map((l, i) => (
                      <div key={l._rid} className="flex items-center gap-2">
                        <Input
                          type="number" min="0" value={l.qty}
                          onChange={(e) => setVLine(l._rid, { qty: e.target.value })}
                          className="h-8 text-right font-mono" placeholder={t("common.qty")}
                          data-testid={`variant-line-qty-${i}`}
                        />
                        <Select value={l.unit} onValueChange={(v) => setVLine(l._rid, { unit: v })}>
                          <SelectTrigger className="h-8 w-36 shrink-0" data-testid={`variant-line-unit-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kg">kg</SelectItem>
                            {pkgOptions.map((p) => (
                              <SelectItem key={p.id} value={p.label}>{p.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button type="button" variant="ghost" size="icon" className="h-8 shrink-0"
                          onClick={() => rmVLine(l._rid)} disabled={variantLines.length <= 1}
                          data-testid={`variant-line-remove-${i}`}>
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <div className="mt-2 flex items-center justify-between gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={addVLine} data-testid="variant-add-line">
                      <Plus className="mr-1.5 h-3.5 w-3.5" /> {t("prod.addPackaging")}
                    </Button>
                    <span className="font-mono text-xs text-muted-foreground" data-testid="variant-total-kg">
                      {t("prod.yieldTotalKg", { kg: fmt(variantTotalKg, 2) })}
                    </span>
                  </div>
                  {variantHasNoNet && <p className="mt-1 text-[11px] text-[hsl(var(--danger))]">{t("prod.yieldSomeNoNet")}</p>}

                  <div className="mt-2.5 flex justify-end">
                    <Button size="sm" className="h-9" onClick={submitVariantAllocation}
                      disabled={variantBusy || !variantProduct || variantTotalKg <= 0 || variantHasNoNet}
                      data-testid="variant-stock-add">
                      <ArrowDownToLine className="mr-1.5 h-4 w-4" /> {t("prod.variantStockAdd")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}


          <h3 className="mt-5 text-sm font-semibold">{t("prod.milkInputs")}</h3>
          {edit && canEditMilk ? (
            <div className="mt-2 space-y-1.5" data-testid="milk-inputs-edit">
              {(draft.milk_inputs || []).map((i, idx) => (
                <div key={i.milk_batch_id || i.batch_code || idx} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 text-sm">
                  <span className="font-mono text-xs text-muted-foreground">{i.batch_code}</span>
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      value={i.qty_l}
                      onChange={(e) => setDraft((d) => ({
                        ...d,
                        milk_inputs: d.milk_inputs.map((x, j) => (j === idx ? { ...x, qty_l: e.target.value } : x)),
                      }))}
                      className="h-8 w-28 text-right font-mono"
                      data-testid={`milk-input-qty-${idx}`}
                    />
                    <span className="text-xs text-muted-foreground">L</span>
                  </div>
                </div>
              ))}
              <div className="flex justify-between px-3 pt-1 text-xs">
                <span className="text-muted-foreground">{t("prod.milkRecalcNote")}</span>
                <span className="font-mono font-medium">
                  {fmt((draft.milk_inputs || []).reduce((s, i) => s + (Number(i.qty_l) || 0), 0))} L
                </span>
              </div>
            </div>
          ) : (
            <div className="mt-2 space-y-1.5">
              {detail.milk_inputs.map((i, idx) => (
                <div key={i.batch_id || i.batch_code || idx} className="flex justify-between rounded-lg border border-border px-3 py-2 text-sm">
                  <span className="font-mono text-xs text-muted-foreground">{i.batch_code}</span>
                  <span className="font-mono">{fmt(i.qty_l)} L</span>
                </div>
              ))}
            </div>
          )}

          {/* By-products */}
          {edit ? (
            <div className="mt-5">
              <ByproductEditor
                value={draft.byproducts}
                onChange={(bp) => setDraft((d) => ({ ...d, byproducts: bp }))}
                productType={detail.product_type}
              />
            </div>
          ) : (
            namedByproducts.length > 0 && (
              <>
                <h3 className="mt-5 text-sm font-semibold">{t("prod.byproducts")}</h3>
                <div className="mt-2 space-y-1.5" data-testid="detail-byproducts">
                  {namedByproducts.map((b, idx) => (
                    <div key={`${b.name}-${idx}`} className="flex justify-between rounded-lg border border-border px-3 py-2 text-sm">
                      <span>
                        {b.name}
                        {b.lot_code && (
                          <span className="ml-1.5 font-mono text-xs text-muted-foreground" data-testid={`detail-byproduct-code-${idx}`}>{b.lot_code}</span>
                        )}
                      </span>
                      <span className="font-mono">{b.yield_kg != null ? `${fmt(b.yield_kg)} ${b.unit === "l" ? "L" : "kg"}` : "-"}</span>
                    </div>
                  ))}
                </div>
              </>
            )
          )}

          {/* Subproduct inputs consumed as raw material (read-only) */}
          {(detail.subproduct_inputs || []).length > 0 && (
            <>
              <h3 className="mt-5 text-sm font-semibold">{t("prod.spInputsTitle")}</h3>
              <div className="mt-2 space-y-1.5" data-testid="detail-subproduct-inputs">
                {detail.subproduct_inputs.map((si, idx) => (
                  <div key={si.lot_id || idx} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm">
                    <span>
                      {si.name}
                      <span className="ml-1.5 font-mono text-xs text-muted-foreground">{si.source_production_code}</span>
                    </span>
                    <span className="font-mono">{fmt(si.qty)} {si.unit === "l" ? "L" : "kg"}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {edit ? (
            <div className="mt-5">
              <RawMaterialInputEditor
                value={draft.rm_inputs}
                onChange={(rm) => setDraft((d) => ({ ...d, rm_inputs: rm }))}
                consumedByLot={Object.fromEntries((detail.raw_material_inputs || []).map((ri) => [ri.lot_id, Number(ri.qty || 0)]))}
              />
            </div>
          ) : (
            (detail.raw_material_inputs || []).length > 0 && (
            <>
              <h3 className="mt-5 text-sm font-semibold">{t("rawMaterials.traceTitle")}</h3>
              <div className="mt-2 space-y-1.5" data-testid="detail-raw-material-inputs">
                {detail.raw_material_inputs.map((ri, idx) => (
                  <div key={ri.lot_id || idx} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm">
                    <span>
                      {ri.type_name}
                      <span className="ml-1.5 font-mono text-xs text-muted-foreground">{ri.lot_code}</span>
                      {ri.supplier_lot_no ? <span className="ml-1.5 text-xs text-muted-foreground">· {t("rawMaterials.supplierLot")}: <span className="font-mono">{ri.supplier_lot_no}</span></span> : null}
                      {ri.supplier_name ? <span className="ml-1.5 text-xs text-muted-foreground">· {ri.supplier_name}</span> : null}
                    </span>
                    <span className="font-mono">{fmt(ri.qty)} {ri.unit}</span>
                  </div>
                ))}
              </div>
            </>
            )
          )}

          {tpl && (
            <>
              <div className="mt-5 flex items-center justify-between">
                <h3 className="text-sm font-semibold">{t("prod.qualityControl", { name: tpl.name })}</h3>
                {edit && canEditStructure && (
                  <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} data-testid="qc-add-field-button">
                    <Plus className="mr-1.5 h-4 w-4" /> {t("prod.addField")}
                  </Button>
                )}
              </div>
              {!edit ? (
                <div className="mt-2 overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("prod.field")}</TableHead>
                        <TableHead className="text-right">{t("prod.value")}</TableHead>
                        <TableHead className="text-right">{t("prod.range")}</TableHead>
                        <TableHead>{t("common.status")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tpl.fields.map((f) => {
                        const v = detail.qc_values?.[f.id];
                        const st = fieldStatus(f, v);
                        return (
                          <TableRow key={f.id}>
                            <TableCell>{f.name}</TableCell>
                            <TableCell className="text-right font-mono">
                              {v === undefined || v === "" ? "-" : `${typeof v === "boolean" ? (v ? t("common.yes") : t("common.no")) : v}${f.unit ? " " + f.unit : ""}`}
                              {st === "bad" && (detail.qc_corrective || {})[f.id] && (
                                <div className="mt-0.5 max-w-[240px] whitespace-normal text-right font-sans text-[11px] text-muted-foreground" data-testid={`qc-corrective-view-${f.id}`}>
                                  {t("prod.correctiveAction")}: {(detail.qc_corrective || {})[f.id]}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs text-muted-foreground">{f.min != null || f.max != null ? `${f.min ?? "–"} – ${f.max ?? "–"}` : "-"}</TableCell>
                            <TableCell>{st ? <StatusBadge status={st} /> : "-"}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="mt-2 space-y-3">
                  {activeFields.map((f) => {
                    const st = fieldStatus(f, draft.qc_values[f.id]);
                    return (
                      <div key={f.id} className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <Label>
                            {f.name}
                            {(f.min != null || f.max != null) && (
                              <span className="ml-2 font-mono text-xs text-muted-foreground">[{f.min ?? "–"} – {f.max ?? "–"}]</span>
                            )}
                          </Label>
                          <div className="flex items-center gap-2">
                            {st === "bad" && <StatusBadge status="bad" />}
                            {st === "ok" && <StatusBadge status="ok" />}
                            {canEditStructure && (
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeField(f.id)} data-testid={`qc-remove-field-${f.id}`}>
                                <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                              </Button>
                            )}
                          </div>
                        </div>
                        {renderQcInput(f, draft.qc_values[f.id], (v) => setVal(f.id, v), st, t)}
                        {st === "bad" && f.type === "select" && (
                          <div className="space-y-1 rounded-lg border border-[hsl(var(--danger)/0.4)] bg-[hsl(var(--danger)/0.05)] p-2.5">
                            <Label className="text-xs font-medium text-[hsl(var(--danger))]">
                              {t("prod.correctiveAction")} <span aria-hidden="true">*</span>
                            </Label>
                            <Textarea rows={2} value={(draft.qc_corrective || {})[f.id] || ""}
                              onChange={(e) => setCorr(f.id, e.target.value)}
                              placeholder={t("prod.correctivePh")}
                              data-testid={`qc-corrective-edit-${f.id}`} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {!edit && detail.notes && <p className="mt-4 text-sm text-muted-foreground">{t("prod.notesPrefix")} {detail.notes}</p>}
          {edit && (
            <div className="mt-4 space-y-1.5">
              <Label>{t("common.notes")}</Label>
              <Textarea rows={2} value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} data-testid="edit-notes-input" />
            </div>
          )}
        </div>
      </DrawerContent>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("prod.addQcField")}</DialogTitle></DialogHeader>
          <AddFieldForm onAdd={addField} onCancel={() => setAddOpen(false)} />
        </DialogContent>
      </Dialog>
    </Drawer>
  );
}

function AddFieldForm({ onAdd, onCancel }) {
  const { t } = useT();
  const [f, setF] = useState({ name: "", type: "number", unit: "", min: "", max: "", options: "" });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const showRange = ["number", "temperature", "ph", "time"].includes(f.type);
  const submit = () => {
    if (!f.name.trim()) { toast.error(t("prod.fieldNameRequired")); return; }
    onAdd({
      id: qcUid(),
      name: f.name,
      type: f.type,
      unit: f.unit || "",
      required: false,
      min: f.min === "" ? null : Number(f.min),
      max: f.max === "" ? null : Number(f.max),
      options: f.type === "select" ? f.options.split(",").map((s) => s.trim()).filter(Boolean) : null,
    });
  };
  return (
    <>
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label>{t("prod.fieldName")}</Label>
          <Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="qc-newfield-name" placeholder={t("prod.fieldNamePlaceholder")} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>{t("prod.type")}</Label>
            <Select value={f.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="qc-newfield-type"><SelectValue /></SelectTrigger>
              <SelectContent>{QC_TYPES.map((ty) => <SelectItem key={ty} value={ty}>{ty}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t("common.unit")}</Label>
            <Input value={f.unit} onChange={(e) => set("unit", e.target.value)} placeholder="°C, %, pH..." />
          </div>
        </div>
        {showRange && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>{t("prod.min")}</Label><Input type="number" value={f.min} onChange={(e) => set("min", e.target.value)} className="text-right font-mono" /></div>
            <div className="space-y-1.5"><Label>{t("prod.max")}</Label><Input type="number" value={f.max} onChange={(e) => set("max", e.target.value)} className="text-right font-mono" /></div>
          </div>
        )}
        {f.type === "select" && (
          <div className="space-y-1.5"><Label>{t("prod.options")}</Label><Input value={f.options} onChange={(e) => set("options", e.target.value)} placeholder="Pass, Fail" /></div>
        )}
      </div>
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onCancel}>{t("common.cancel")}</Button>
        <Button onClick={submit} data-testid="qc-newfield-add">{t("prod.addField")}</Button>
      </DialogFooter>
    </>
  );
}
